<template>
  <div class="material-assistant-step1">
    <div class="oring-assistant__container">
      <div class="oring-assistant__container__cards">
        <div class="card">
          <div class="card-content">
            <div class="card-content__title">
              <h3 class="h4 card-content__title-text">
                {{ $tv('Frontend.Title.ChemicalResistance', 'ORingConfiguratorImport') }}
              </h3>
              <span class="card-content__title-icon">
                <ActionInfoFilledIcon
                  @click="openPopup"
                  @keydown.enter="openPopup"
                  @keydown.space="openPopup"
                  tabindex="0"
                />
              </span>
              <PopupBase
                v-if="showPopup"
                :show="showPopup"
                @close-pop-up="closePopup"
                :size="'small'"
              >
                <PopupContent
                  :title="$tv('Frontend.Title.ChemicalResistance', 'ORingConfiguratorImport')"
                  :content="$tv('Frontend.Info.ChemicalResistanceOne', 'ORingConfiguratorImport')"
                />
              </PopupBase>
            </div>
            <div class="card-content__description">
              {{ $tv('Frontend.Message.ChemicalResistance', 'ORingConfiguratorImport') }}
            </div>
            <div class="card-content__container">
              <div class="filter-item-wrapper">
                <label
                  class="label"
                  id="fluidOptionsLabel"
                  for="fluidOptions"
                >
                  {{ $tv('selectUpToThreeMedia', 'ORingConfigurator') }}
                </label>
                <div class="material-assistant-step1__multi-select-wrapper">
                  <MultiSelectDropDown
                    id="fluidOptions"
                    aria-labelledby="fluidOptionsLabel"
                    :data-items="oringMaterialAssistantFluidOptions"
                    v-model="selectedFluids"
                    :text-field="'fluidName'"
                    :data-item-key="'fluidId'"
                    class="multi-select"
                    :filterable="true"
                    :is-sorted="true"
                    :disabled="loadingFluids"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionInfoFilledIcon from '~/assets/icons/action_info_filled.svg?component'
import PopupBase from '~/components/popup/PopupBase.vue'
import PopupContent from '~/components/oring-calculator/PopupContent.vue'
import MultiSelectDropDown from '~/components/input-components/MultiSelectDropDown.vue'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import type {FluidItem} from '@/types/oring-api-responses'
import useI18nExtended from '~/composables/usei18nExtended'

const validate = () => oringMaterialAssistantFluidIds.value.length > 0

const showPopup = ref(false)
function openPopup() {
  showPopup.value = true
}
function closePopup() {
  showPopup.value = false
}
const {
  oringMaterialAssistantFluidIds,
  oringMaterialAssistantFluidOptions,
  loadingFluids
} = storeToRefs(useORingCalculatorStore())

// Bridge between store IDs and component objects
const selectedFluids = computed<FluidItem[]>({
  get: () => oringMaterialAssistantFluidOptions.value.filter(f => oringMaterialAssistantFluidIds.value.includes(f.fluidId)),
  set: (fluids: FluidItem[]) => {
    // Show toast if user tries to select more than 3 fluids
    if (fluids.length > 3) {
      const {$toast} = useNuxtApp()
      ;($toast as any).warning(i18n.tv('selectUpToThreeMedia', 'ORingConfigurator'))
    }
    // Limit selection to maximum 3 fluids
    const limitedFluids = fluids.slice(0, 3)
    oringMaterialAssistantFluidIds.value = limitedFluids.map(f => f.fluidId)
  }
})
const i18n = useI18nExtended()

onMounted(async () => {
  await useORingCalculatorStore().loadOringFluidOptions()
  // Preselect 'air' (fluidId: 1) if not already selected
  if (!oringMaterialAssistantFluidIds.value.includes(1)) {
    oringMaterialAssistantFluidIds.value = [1]
  }
})

defineExpose({validate})
</script>

<style lang="scss">
.material-assistant-step1 {
  @include breakpoint-down($md) {
    .oring-assistant__container {
      margin-bottom: 1.5rem;
    }
  }

  .oring-assistant__container__cards .card .card-content__container {
    @include breakpoint-down($md) {
      max-width: unset;
    }
  }

  // Local wrapper to scope styles for this component only
  .material-assistant-step1__multi-select-wrapper {
    width: 31.25rem; // 500px
    max-width: 100%;
    box-sizing: border-box;
    min-width: 0; // allow shrinking in flex parents

    // Inner wrapper rendered by MultiSelectDropDown
    .dropdown-wrapper.multi-select {
      width: 31.25rem; // 500px
      max-width: 31.25rem;
      display: block;
      box-sizing: border-box;

      // Make Kendo control fill the wrapper
      .k-multiselect,
      .k-input,
      .k-input-inner {
        width: 100%;
      }

      // Allow chips to wrap to multiple lines
      .k-input-values {
        display: flex;
        flex-wrap: wrap;
      }
    }

    @include breakpoint-down($md) {
      width: 100%;

      .dropdown-wrapper.multi-select {
        width: 100%;
        max-width: 100%;

        .k-multiselect,
        .k-input,
        .k-input-inner {
          width: 100%;
        }

        .k-input-values {
          flex-wrap: wrap;
          width: 100%;
        }
      }
    }
  }
}
</style>
